import { useQuery, gql, QueryHookOptions } from '@apollo/client'

import Product from '../modules/Product'

import { BaseProductPayload, BaseProduct, Pagination } from '../../types'

type Properties =
  | 'id'
  | 'name'
  | 'description'
  | 'shortDescription'
  | 'longDescription'
  | 'sku'
  | 'mpn'
  | 'altMpn'
  | 'brand'
  | 'category'
  | 'salePrice'
  | 'listPrice'
  | 'shippingCost'
  | 'availability'
  | 'quantity'
  | 'enabled'
  | 'merchantEnabled'
  | 'merchantGroup'
  | 'offline'
  | 'images'
  | 'shippingRate'
  | 'addedAt'
  | 'updatedAt'
  | 'store'
  | 'path'
  | 'leadTime'
  | 'leadTimeDate'
  | 'fitsWithMachines'
  | 'isSource'
  | 'sourceProduct'
  | 'isCustom'

type ProductPayload = Required<Pick<BaseProductPayload, Properties>>
export type Product = Required<Pick<BaseProduct, Properties>>

interface Data {
  productsSearch: {
    products: ProductPayload[]
    pagination: Pagination
  }
}

interface Variables {
  filters: string[][]
  page?: number
}

const query = gql`
  query ProductsSearch($page: Int, $filters: [[String]]) {
    productsSearch(page: $page, filters: $filters) {
      pagination {
        totalPages
        totalResults
      }
      products {
        id
        name
        description
        shortDescription
        longDescription
        mpn
        altMpn
        sku
        isSource
        brand {
          id
          name
        }
        category {
          id
          name
        }
        salePrice
        listPrice
        shippingCost
        availability
        leadTime
        leadTimeDate
        enabled
        merchantEnabled
        merchantGroup
        offline
        images {
          variants {
            name
            url
          }
        }
        addedAt
        quantity
        shippingRate
        updatedAt
        path
        isCustom {
          name
          mpn
          altMpn
          sku
          shippingCost
          shortDescription
          longDescription
          availability
          leadTime
          leadTimeDate
          category
          brand
          image
        }
        store {
          id
          name
        }
        fitsWithMachines {
          make
          model
          year
        }
        sourceProduct {
          name
          mpn
          altMpn
          sku
          shippingCost {
            amount
            currency
          }
          shortDescription
          longDescription
          availability
          leadTime
          leadTimeDate
          brand {
            id
            name
          }
          category {
            id
            name
          }
          source {
            name
          }
          images {
            variants {
              name
              url
            }
          }
        }
      }
    }
  }
`

const useProducts = (options?: QueryHookOptions<Data, Variables>) => {
  const result = useQuery<Data, Variables>(query, { ...options, fetchPolicy: 'no-cache' })

  const data = result.data && {
    ...result.data,
    productsSearch: {
      ...result.data.productsSearch,
      products: result.data.productsSearch.products.map(Product.fromPayload),
    },
  }

  return { ...result, data }
}

export default useProducts
